import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Dialog } from '@/shared/ui/Dialog';
import { Button } from '@/shared/ui/Button';
import { Input } from '@/shared/ui/Input';
import { useToast } from '@/shared/ui/toast';
import { ApiException } from '@/shared/api/client';
import { customerSchema, type CustomerFormValues } from '@/shared/schemas/customer.schema';
import { useCreateCustomer, useUpdateCustomer } from './customers.api';
import type { Customer } from '@/shared/api/types';

// Leere Strings entfernen, damit das Backend keine "" statt null bekommt.
function clean(v: CustomerFormValues): Record<string, string> {
  return Object.fromEntries(
    Object.entries(v).filter(([, val]) => val !== undefined && val !== ''),
  ) as Record<string, string>;
}

export function CustomerFormDialog({
  open,
  onClose,
  customer,
}: {
  open: boolean;
  onClose: () => void;
  customer?: Customer | null;
}) {
  const create = useCreateCustomer();
  const update = useUpdateCustomer();
  const push = useToast((s) => s.push);
  const isEdit = !!customer;

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors, isSubmitting },
  } = useForm<CustomerFormValues>({
    resolver: zodResolver(customerSchema),
    values: {
      companyName: customer?.companyName ?? '',
      billingAddress: customer?.billingAddress ?? '',
      email: customer?.email ?? '',
      phone: customer?.phone ?? '',
      notes: customer?.notes ?? '',
    },
  });

  const onSubmit = async (values: CustomerFormValues) => {
    try {
      const input = clean(values);
      if (isEdit && customer) {
        await update.mutateAsync({ id: customer.id, input });
        push('Kunde aktualisiert.');
      } else {
        await create.mutateAsync(input as CustomerFormValues);
        push('Kunde angelegt.');
      }
      reset();
      onClose();
    } catch (e) {
      push(e instanceof ApiException ? e.message : 'Speichern fehlgeschlagen.', 'error');
    }
  };

  return (
    <Dialog open={open} onClose={onClose} title={isEdit ? 'Kunde bearbeiten' : 'Neuer Kunde'}>
      <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
        <Input label="Firmenname *" {...register('companyName')} error={errors.companyName?.message} />
        <Input label="Rechnungsadresse" {...register('billingAddress')} error={errors.billingAddress?.message} />
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <Input label="E-Mail" type="email" {...register('email')} error={errors.email?.message} />
          <Input label="Telefon" {...register('phone')} error={errors.phone?.message} />
        </div>
        <div className="space-y-1">
          <label className="block text-sm font-medium text-slate-700">Notizen</label>
          <textarea
            {...register('notes')}
            rows={3}
            className="w-full rounded-lg border border-slate-300 px-3 py-2.5 focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500"
          />
        </div>
        <div className="flex justify-end gap-2 pt-2">
          <Button type="button" variant="secondary" onClick={onClose}>
            Abbrechen
          </Button>
          <Button type="submit" loading={isSubmitting}>
            {isEdit ? 'Speichern' : 'Anlegen'}
          </Button>
        </div>
      </form>
    </Dialog>
  );
}
